// From: https://github.com/kraaden/autocomplete
import autocomplete from "autocompleter";
import { populateSearchResults, updateQueryInUrl } from "./search_page";

// TODO: if on maps page and user searches via header, update via JS, don't load
// new page

// prefix for our autocomplete options
const SEARCH_SUFFIX = " - Search";

const listIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="icon_collection">
    <rect width="20" height="12" x="2" y="10" class="primary" rx="2"></rect>
    <path class="secondary" d="M20 8H4c0-1.1.9-2 2-2h12a2 2 0 0 1 2 2zm-2-4H6c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2z"></path>
  </svg>`;

const mapIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="icon_photo">
    <path class="primary" d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2zm9 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"></path>
    <path class="secondary" d="M15.3 12.3a1 1 0 0 1 1.4 0l2 2a1 1 0 0 1 .3.7v3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-3a1 1 0 0 1 .3-.7l4-4a1 1 0 0 1 1.4 0l3.3 3.29 1.3-1.3z"></path>
  </svg>`;

const searchIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-8 mr-4 icon_search">
    <circle cx="10" cy="10" r="7" class="primary"></circle>
    <path class="secondary" d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"></path>
  </svg>`;

function renderAutocompleteOption(item) {
  const div = document.createElement("div");

  // add icon for collection or single map
  const svgContainer = document.createElement("span");
  if (item.type === "search") {
    svgContainer.innerHTML = searchIcon;
  } else if (item.type === "map") {
    svgContainer.innerHTML = mapIcon;
  } else {
    svgContainer.innerHTML = listIcon;
  }
  div.appendChild(svgContainer);

  // add text label identifying option
  const label = document.createElement("span");
  label.textContent = item.label;
  div.appendChild(label);

  return div;
}

// registers our search with google analytics
export function fireSearchEvent(query) {
  window.gtag("event", "search", {
    search_term: query
  });
}

// check the query to see if it matches a single result in our list of results
// (which includes individual maps, collections, locations, etc)
export function matchQueryToEntry(untrimmedQuery) {
  const query = untrimmedQuery.trim();
  return (option) => {
    const entry = option[0];
    // if query includes a space, it is multiple words, so we want to see
    // if the query is included in the comparison string. Eg. Canyon National
    // Park should return Grand Canyon and Bryce Canyon
    if (query.includes(" ")) {
      return entry.toLowerCase().includes(query.toLowerCase());
    } else {
      // if we just have a single-word query, look to see if any of the maps
      // names contain a word that starts with that single-word query.
      // Eg. "Canyon" should hit for Bryce Canyon and Grand Canyon but
      // "ver" should not hit for "River"
      // Split search entrys by word and then match on the beginning of any of the words
      return entry.toLowerCase()
        .split(/\s/g)
        .find(word => word.startsWith(query.toLowerCase()));
    }
  };
}

function sortOptionsByQuery(untrimmedQuery) {
  return function sortOptions(a, b) {
    const firstEntry = a[0].toLowerCase();
    const secondEntry = b[0].toLowerCase();
    const query = untrimmedQuery.replace(/\s+/g, ' ').trim().toLowerCase();
    // if either the first or the second entry start with the string, bump
    // that entry to the front, otherwise, sort alphabetically
    if (firstEntry.startsWith(query) && !secondEntry.startsWith(query)) {
      return -1;
    } else if (secondEntry.startsWith(query) && !firstEntry.startsWith(query)) {
      return 1;
    }
    return firstEntry > secondEntry ? 1 : -1;
  };
}

// used by autocomplete to filter all maps to autocomplete options
// creates a list of results, each result being a POJO with a `label` key
function filterMaps(query) {
  let matches = window.rambleMaps.autocompleteOptions.filter(matchQueryToEntry(query));

  // sort our matches so that anything that is a string match that starts with our
  // query gets pulled to the front, the rest is sorted alphabetically
  matches.sort(sortOptionsByQuery(query));

  // Create an item in autocomplete dropdown for a regular query, not
  // matching exactly on any of our maps/collections/locations/etc
  const rawQueryResult = {
    label: `${query}${SEARCH_SUFFIX}`,
    type: "search"
  };

  return [rawQueryResult, ...matches.map(match => ({
    label: match[0],
    type: match[1] === 1 ? "map" : "collection",
    // if matching maps count is 1 and we have a path, provide it to the query
    // object. If the user manually chooses an option from the autocomplete
    // dropdown that has a path, we will go to it instead of showing results
    path: match[1] === 1 && match[2]
  }))];
}

// load the maps page with our query
function submitQuery(query) {
  window.location.href = `/search?q=${encodeURIComponent(query)}`;
}

function buildAutocompleteResultsForQuery(query, updateAutocompleter) {
  if (query.length > 0) {
    const results = filterMaps(query);
    updateAutocompleter(results);
  } else {
    updateAutocompleter([]);
  }
}

function onAutocompleteSelect(query) {
  // if our query has a path, we want to go to it instead of returning the
  // result
  if (query.path) {
    window.location.href = `/${query.path}`;
    return false;
  }

  const cleanedQuery = query.label.includes(SEARCH_SUFFIX)
    ? query.label.split(SEARCH_SUFFIX)[0]
    : query.label;

  submitQuery(cleanedQuery, "search");
}

function initDesktopHeaderSearch() {
  const desktopSearchInput = document.getElementById("js_desktop_header_search_input");

  if (desktopSearchInput) {
    // when a user clicks on the search button, we don't want to use autocompleter
    // we want to use the input value
    const desktopSearchButton = document.getElementById("js_desktop_header_search_button");
    desktopSearchButton.addEventListener("click", () => {
      submitQuery(desktopSearchInput.value);
    });

    // instantiate autocomplete on our input field
    autocomplete({
      input: desktopSearchInput,
      minLength: 0,
      fetch: buildAutocompleteResultsForQuery,
      render: renderAutocompleteOption,
      className: "desktop_header_search_autocomplete",
      showOnFocus: true,
      onSelect: onAutocompleteSelect,
    });
  }
}

function initMobileSearch() {

  function hideMobileSearch(searchContainer, searchInput) {
    searchContainer.classList.remove("active");
    document.body.classList.remove("no_scroll");
    searchInput.value = "";
  }

  const mobileSearch = document.getElementById("js_mobile_search");
  if (mobileSearch) {
    const mobileSearchInput = document.getElementById("js_mobile_search_input");
    const mobileSearchContainer = document.getElementById("js_mobile_search_container");

    // open mobile search interface when magnifying glass is clicked
    const mobileSearchIcon = document.getElementById("js_mobile_search_icon");
    const headerHeight = document.querySelector(".header").offsetHeight;
    const banner = document.getElementById("js_free_shipping");
    const bannerHeight = banner ? banner.offsetHeight : 0;
    mobileSearchIcon.addEventListener("click", () => {
      // position the search box below the banner, make it the same height as the
      // header less the banner
      mobileSearch.style.height = `${headerHeight - bannerHeight}px`;
      mobileSearch.style.marginTop = `${bannerHeight}px`;
      mobileSearchContainer.classList.add("active");
      mobileSearchInput.focus();
      document.body.classList.add("no_scroll");
    });

    // hide search interface if user clicks outside of input or button
    mobileSearchContainer.addEventListener("click", (e) => {
      if (e.target.id === "js_mobile_search_container") {
        hideMobileSearch(mobileSearchContainer, mobileSearchInput);
      }
    });

    const mobileSearchClose = document.getElementById("js_mobile_search_close");
    mobileSearchClose.addEventListener("click", () => {
      hideMobileSearch(mobileSearchContainer, mobileSearchInput);
    });

    // instantiate autocomplete on our input field
    autocomplete({
      input: mobileSearchInput,
      minLength: 0,
      fetch: buildAutocompleteResultsForQuery,
      onSelect: onAutocompleteSelect,
      className: "mobile_search_autocomplete",
      render: renderAutocompleteOption,
      showOnFocus: true,
      customize: (input, inputRect, container) => {
        container.style.width = "100%";
        container.style.left = "0";
      }
    });
  }
}

function initMapsPageSearch() {
  const input = document.getElementById("js_maps_page_filter");

  if (input) {
    // handle search button click
    const searchButton = document.getElementById("js_maps_page_search_button");
    searchButton.addEventListener("click", () => {
      submitQuery(input.value);
    });

    autocomplete({
      input,
      minLength: 0,
      fetch: buildAutocompleteResultsForQuery,
      render: renderAutocompleteOption,
      showOnFocus: true,
      onSelect: onAutocompleteSelect,
    });
  }
}

function initSearchPageSearch() {
  const input = document.getElementById("js_search_page_filter");

  if (input) {
    // handle search button click, perform regular search
    const searchButton = document.getElementById("js_search_page_search_button");
    searchButton.addEventListener("click", () => {
      updateQueryInUrl(input.value);
      populateSearchResults(input.value);
    });

    autocomplete({
      input,
      minLength: 0,
      fetch: buildAutocompleteResultsForQuery,
      render: renderAutocompleteOption,
      showOnFocus: true,
      onSelect: function(query) {
        // if our query has a path, we want to go to it instead of returning the
        // result
        if (query.path) {
          window.location.href = `/${query.path}`;
          return false;
        }

        const cleanedQuery = query.label.includes(SEARCH_SUFFIX)
          ? query.label.split(SEARCH_SUFFIX)[0]
          : query.label;

        updateQueryInUrl(cleanedQuery);
        populateSearchResults(cleanedQuery);
      }
    });
  }
}

// Initialize search functionality for header bar
export default function search() {
  initDesktopHeaderSearch();
  initMobileSearch();
  initMapsPageSearch();
  initSearchPageSearch();
}
